import React, { useEffect, useMemo, useRef, useState } from 'react';

import { styled } from 'styled-components';

import { MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { Input } from 'components';
import { CloseCircle } from 'iconsax-react';
import FilterContains from 'images/filter-contains.svg';
import FilterEndsWith from 'images/filter-end-with.svg';
import FilterEquals from 'images/filter-equals.svg';
import FilterNoEquals from 'images/filter-no-equals.svg';
import FilterNotEquals from 'images/filter-not-equal.svg';
import FilterStartsWith from 'images/filter-start-with.svg';

import { ColumnFilteringProps } from '../types';

import { _, useTranslation } from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';

import { APIStringOperators, APIStringSearchFilter } from 'types/api';

const RESET_FILTER = '__reset__';

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  padding: 0.1rem 0 0.3rem 0;

  & input {
    padding: 0.1rem !important;
  }
`;

const ConditionSelect = styled(Select)`
  min-width: 2.5rem;
  max-width: 5rem;
  font-size: 0.75rem;

  & .MuiSelect-select {
    display: flex;
    align-items: center;
    padding: 0.1rem 0.25rem !important;
  }
`;

const FilterIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: invert(32%) sepia(95%) saturate(1200%) hue-rotate(162deg)
    brightness(88%) contrast(101%);
`;

type OperatorConfig = {
  icon: string | null;
  label: string;
  i18nKey: string;
};

const operatorConfig: Record<APIStringOperators, OperatorConfig> = {
  [APIStringOperators.Equals]: {
    icon: FilterEquals,
    label: '=',
    i18nKey: 'filter.string.equals',
  },
  [APIStringOperators.NotEquals]: {
    icon: FilterNoEquals,
    label: '≠',
    i18nKey: 'filter.string.notEquals',
  },
  [APIStringOperators.NotContains]: {
    icon: FilterNotEquals,
    label: '∌',
    i18nKey: 'filter.string.notContains',
  },
  [APIStringOperators.StartsWith]: {
    icon: FilterStartsWith,
    label: 'A..',
    i18nKey: 'filter.string.startsWith',
  },
  [APIStringOperators.EndsWith]: {
    icon: FilterEndsWith,
    label: '..Z',
    i18nKey: 'filter.string.endsWith',
  },
  [APIStringOperators.Contains]: {
    icon: FilterContains,
    label: '∋',
    i18nKey: 'filter.string.contains',
  },
};

export const AdvancedStringFilter: React.FC<ColumnFilteringProps> = ({
  columnId,
  filterValue,
  onAdvancedFilterChange,
}) => {
  const { t } = useTranslation();
  const filterRef = useRef<HTMLInputElement>(null);

  const currentFilter = filterValue as APIStringSearchFilter | undefined;

  const [condition, setCondition] = useState<APIStringOperators>(
    currentFilter?.condition || APIStringOperators.Contains,
  );
  const [value, setValue] = useState<string>(currentFilter?.value || '');

  const debouncedEmitChange = useMemo(
    () =>
      _.debounce((newCondition: APIStringOperators, newValue: string) => {
        const filter: APIStringSearchFilter = {
          condition: newCondition,
          value: newValue || undefined,
        };
        onAdvancedFilterChange?.(columnId.toString(), filter);
      }, USER_INPUT_DEBOUNCE_TIME_MS),
    [columnId, onAdvancedFilterChange],
  );

  const handleConditionChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;

    if (selectedValue === RESET_FILTER) {
      setCondition(APIStringOperators.Contains);
      setValue('');
      if (filterRef.current) {
        filterRef.current.value = '';
      }
      debouncedEmitChange.cancel();
      onAdvancedFilterChange?.(columnId.toString(), undefined);
      return;
    }

    const newCondition = selectedValue as APIStringOperators;
    setCondition(newCondition);
    debouncedEmitChange(newCondition, value);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    debouncedEmitChange(condition, newValue);
  };

  useEffect(() => {
    if (currentFilter) {
      setCondition(currentFilter.condition || APIStringOperators.Contains);
      setValue(currentFilter.value || '');
      if (filterRef.current) {
        filterRef.current.value = currentFilter.value || '';
      }
    } else {
      setCondition(APIStringOperators.Contains);
      setValue('');
      if (filterRef.current) {
        filterRef.current.value = '';
      }
    }
  }, [currentFilter]);

  return (
    <FilterWrapper>
      <ConditionSelect
        size="small"
        variant="standard"
        value={condition}
        onChange={handleConditionChange}
        sx={{
          '& legend': { display: 'none' },
          '& fieldset': { top: 0 },
        }}
      >
        <MenuItem value={RESET_FILTER} sx={{ fontSize: '0.875rem' }}>
          <Tooltip title={t('filter.clear' as any)} placement="right">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <CloseCircle variant="TwoTone" size="16" color={'#007170'} />
            </span>
          </Tooltip>
        </MenuItem>
        {Object.entries(operatorConfig).map(([op, config]) => (
          <MenuItem key={op} value={op} sx={{ fontSize: '0.875rem' }}>
            <Tooltip title={t(config.i18nKey as any)} placement="right">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {config.icon ? (
                  <FilterIcon src={config.icon} alt={config.label} />
                ) : (
                  config.label
                )}
              </span>
            </Tooltip>
          </MenuItem>
        ))}
      </ConditionSelect>
      <Input
        inputRef={filterRef}
        defaultValue={value}
        onChange={handleValueChange}
        sx={{ flexGrow: 1 }}
      />
    </FilterWrapper>
  );
};
